import React, { useMemo } from 'react';

import { DefectCatalogItem } from 'domain/surveyDataManager/contracts';
import { buildBandLegendItems } from 'domain/surveyDataManager/map/buildRulesFromColorScheme';
import {
  findCatalogMeasurement,
  resolveInitialVisualizationSettings,
} from 'domain/surveyDataManager/visualization/activeLayerVisualizationSettings';
import {
  resolveDefectBandMeaningDescription,
  resolveDefectBandMeaningLabels,
} from 'domain/surveyDataManager/visualization/defectBandMeanings';
import {
  filterLegendItemsToWorstCondition,
  isWorstConditionRuleset,
} from 'domain/surveyDataManager/visualization/worstConditionOnly';
import { ActiveDefectLayer } from 'state/surveyDataManager';

export interface ActiveLayerBandLegendProps {
  readonly layer: ActiveDefectLayer;
  readonly defects: readonly DefectCatalogItem[];
}

function ActiveLayerBandLegend({
  layer,
  defects,
}: ActiveLayerBandLegendProps): JSX.Element | null {
  const catalogDefect = useMemo(
    () => defects.find((defect) => defect.slug === layer.defectSlug) ?? null,
    [defects, layer.defectSlug],
  );

  const catalogRule = useMemo(
    () => findCatalogMeasurement(defects, layer.defectSlug, layer.measurementSlug),
    [defects, layer.defectSlug, layer.measurementSlug],
  );

  const settings = useMemo(
    () =>
      resolveInitialVisualizationSettings({
        catalogRule,
        apiSettings: null,
        savedSettings: layer.visualizationSettings,
        measurementSlug: layer.measurementSlug,
      }),
    [catalogRule, layer.measurementSlug, layer.visualizationSettings],
  );

  const meaningContext = useMemo(
    () => ({
      surveyType: catalogDefect?.surveyType,
      measurementSlug: String(layer.measurementSlug),
      parameter: catalogRule?.parameter,
      code: catalogRule?.code,
      measurementName: catalogRule?.name ?? settings?.ruleName,
      defectType: catalogDefect?.type,
    }),
    [
      catalogDefect?.surveyType,
      catalogDefect?.type,
      catalogRule?.code,
      catalogRule?.name,
      catalogRule?.parameter,
      layer.measurementSlug,
      settings?.ruleName,
    ],
  );

  if (!settings) {
    return null;
  }

  const legendItems = (() => {
    const items = buildBandLegendItems(
      {
        min: settings.colorLow,
        max: settings.colorHigh,
      },
      resolveDefectBandMeaningLabels(meaningContext),
    );
    return isWorstConditionRuleset(String(layer.rulesetSlug))
      ? filterLegendItemsToWorstCondition(items)
      : items;
  })();

  const description =
    settings.ruleDescription.trim() ||
    (isWorstConditionRuleset(String(layer.rulesetSlug))
      ? 'Displays only records exceeding the defined condition threshold.'
      : resolveDefectBandMeaningDescription(meaningContext));

  return (
    <div
      className="sdm-active-layers__legend"
      aria-label={`Colour legend for ${settings.ruleName}`}
    >
      <h4 className="sdm-active-layers__legend-title">Legend: {settings.ruleName}</h4>
      {description ? (
        <p className="sdm-active-layers__legend-description">{description}</p>
      ) : null}
      <ul className="sdm-active-layers__legend-list">
        {legendItems.map((item) => (
          <li key={item.slug} className="sdm-active-layers__legend-item">
            <span
              className="sdm-active-layers__legend-swatch"
              style={{ backgroundColor: item.color }}
              aria-hidden
            />
            <span className="sdm-active-layers__legend-label">{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ActiveLayerBandLegend;
